import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { ArrowRight, ChevronRight, X, AlertTriangle, Lock, Check, Play, RotateCcw, CircleDot } from "lucide-react";
import Icon from "@/components/Icon";
import VideoModal from "@/components/VideoModal";
import FirewatchCourse from "@/components/FirewatchCourse";
import { PIPELINE, BASES } from "@/lib/data";
import { useAuth } from "@/context/AuthContext";

const STORAGE_KEY = "reentry_journey_progress";

export default function Pathway() {
  const { user, updateProgress } = useAuth();
  const isCandidate = user?.role === "candidate";
  const [active, setActive] = useState(null);
  const [checked, setChecked] = useState({});
  const [base, setBase] = useState(BASES[0]);
  const [progress, setProgress] = useState(0); // number of completed stages 0..6
  const [videoSrc, setVideoSrc] = useState(null);
  const [courseOpen, setCourseOpen] = useState(false);

  useEffect(() => {
    if (isCandidate) {
      setProgress(user.journey_progress || 0);
    } else {
      const saved = Number(localStorage.getItem(STORAGE_KEY));
      if (!Number.isNaN(saved)) setProgress(saved);
    }
  }, [isCandidate, user]);

  const persist = (n) => {
    setProgress(n);
    if (isCandidate) { updateProgress(n).catch(() => {}); }
    else { localStorage.setItem(STORAGE_KEY, String(n)); }
  };
  const pct = Math.round((progress / PIPELINE.length) * 100);

  const statusOf = (i) => (i < progress ? "done" : i === progress ? "current" : "locked");

  const openStep = (s, i) => {
    if (statusOf(i) === "locked") {
      toast.error("Stage locked", { description: `Complete "${PIPELINE[i - 1].label}" to unlock ${s.label}.` });
      return;
    }
    setActive({ ...s, index: i });
    setChecked({});
  };

  const completeStep = (i) => {
    if (i === progress) {
      persist(i + 1);
      toast.success(`${PIPELINE[i].label} complete`, {
        description: i + 1 < PIPELINE.length ? `${PIPELINE[i + 1].label} is now unlocked.` : "You're Shipyard Ready — time for placement!",
      });
    }
    setActive(null);
  };

  const step = active;

  return (
    <section id="pathway" className="relative py-24 sm:py-32 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10">
          <div className="font-mono-tech text-[11px] tracking-[0.25em] text-[#FF5E1E] mb-3">[ SEC—PIPELINE — GET SHIPYARD READY ]</div>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <h2 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl max-w-3xl">Your interactive career journey</h2>
              <p className="mt-4 max-w-2xl text-[#8FA3B8]">Each stage unlocks the next. Click a stage to open it, work the checklist, watch the training, then <span className="text-white font-semibold">complete it to unlock what's next</span>. Your progress is saved.</p>
            </div>
            <div className="min-w-[220px]">
              <div className="flex items-center justify-between font-mono-tech text-[10px] tracking-[0.2em] text-[#8FA3B8] mb-2">
                <span>SHIPYARD READY</span><span className="text-[#FF5E1E]">{pct}%</span>
              </div>
              <div className="h-2 bg-[#10161F] overflow-hidden">
                <motion.div animate={{ width: `${pct}%` }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }} className="h-full bg-gradient-to-r from-[#FF5E1E] to-[#FFB800]" />
              </div>
              {progress > 0 && (
                <button data-testid="reset-journey-button" onClick={() => { persist(0); toast("Journey reset"); }} className="mt-2 inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.15em] text-[#8FA3B8] hover:text-white transition-colors">
                  <RotateCcw size={11} /> RESET JOURNEY
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Journey node track */}
        <div className="hidden lg:flex items-center mb-8">
          {PIPELINE.map((s, i) => {
            const st = statusOf(i);
            return (
              <div key={s.id} className="flex items-center flex-1 last:flex-none">
                <button
                  onClick={() => openStep(s, i)}
                  data-testid={`journey-node-${s.key}`}
                  className={`relative h-11 w-11 shrink-0 grid place-items-center border-2 transition-colors ${
                    st === "done" ? "bg-[#10B981] border-[#10B981] text-[#0B0F14]"
                    : st === "current" ? "bg-[#FF5E1E] border-[#FF5E1E] text-[#0B0F14] ember-glow"
                    : "bg-[#10161F] border-white/15 text-[#8FA3B8]"
                  }`}
                >
                  {st === "done" ? <Check size={20} /> : st === "locked" ? <Lock size={16} /> : <Icon name={s.icon} size={18} />}
                  {st === "current" && <span className="absolute -inset-1 border border-[#FF5E1E]/50 animate-ping" />}
                </button>
                {i < PIPELINE.length - 1 && (
                  <div className="flex-1 h-0.5 mx-1 bg-white/10 relative overflow-hidden">
                    <motion.div initial={false} animate={{ width: i < progress ? "100%" : "0%" }} transition={{ duration: 0.5 }} className="absolute inset-y-0 left-0 bg-[#10B981]" />
                    {i === progress - 0 && i === progress && <span className="absolute inset-y-0 left-0 w-1/3 beam" />}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Stage cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
          {PIPELINE.map((s, i) => {
            const st = statusOf(i);
            return (
              <motion.button
                key={s.id}
                data-testid={`pathway-step-card-${s.key}`}
                onClick={() => openStep(s, i)}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: i * 0.06, duration: 0.5 }}
                className={`group relative text-left border p-5 transition-colors min-h-[200px] flex flex-col ${
                  st === "locked" ? "bg-[#0d121a] border-white/5 opacity-70"
                  : st === "done" ? "bg-[#10161F] border-[#10B981]/40 hover:border-[#10B981]"
                  : "bg-[#141c28] border-[#FF5E1E]/50 hover:border-[#FF5E1E]"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono-tech text-[10px] tracking-[0.2em] text-[#8FA3B8]">{s.step}</span>
                  <StatusPill st={st} tag={s.tag} />
                </div>
                <span className={`mt-4 h-11 w-11 grid place-items-center transition-colors ${
                  st === "done" ? "bg-[#10B981] text-[#0B0F14]"
                  : st === "current" ? "bg-[#FF5E1E] text-[#0B0F14]"
                  : "bg-[#182230] text-[#8FA3B8]"
                }`}>
                  {st === "locked" ? <Lock size={20} /> : st === "done" ? <Check size={22} /> : <Icon name={s.icon} size={22} />}
                </span>
                <h3 className="mt-4 font-display font-bold uppercase text-lg leading-tight">{s.label}</h3>
                <p className="mt-1.5 text-xs text-[#8FA3B8] leading-snug flex-1">{s.tagline}</p>
                <span className={`mt-3 flex items-center gap-1 text-[11px] font-mono-tech tracking-wide transition-colors ${
                  st === "locked" ? "text-[#5b6b7d]" : st === "done" ? "text-[#10B981]" : "text-[#FF5E1E]"
                }`}>
                  {st === "locked" ? "LOCKED" : st === "done" ? "REVIEW" : "OPEN"}
                  {st !== "locked" && <ChevronRight size={13} className="group-hover:translate-x-0.5 transition-transform" />}
                </span>
              </motion.button>
            );
          })}
        </div>

        <div className="mt-6 flex items-center gap-2 text-xs text-[#8FA3B8] font-mono-tech tracking-wide">
          <span className="h-1.5 w-1.5 bg-[#10B981] rounded-full" /> WE DON'T JUST FIND JOBS — WE BUILD SKILLED WORKERS
        </div>
      </div>

      {/* Stage modal */}
      <Dialog open={!!step} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-2xl bg-[#10161F] border-white/10 text-[#F4F6F8] rounded-none p-0 gap-0 max-h-[90vh] overflow-y-auto [&>button]:hidden">
          {step && (
            <>
              <DialogHeader className="p-6 border-b border-white/10 sticky top-0 bg-[#10161F] z-10">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <span className="h-12 w-12 grid place-items-center bg-[#FF5E1E] text-[#0B0F14] shrink-0"><Icon name={step.icon} size={24} /></span>
                    <div>
                      <div className="font-mono-tech text-[10px] tracking-[0.2em] text-[#8FA3B8]">STEP {step.step} · {step.tag} · {step.eta}</div>
                      <DialogTitle className="font-display font-black uppercase text-2xl tracking-tight">{step.label}</DialogTitle>
                    </div>
                  </div>
                  <button data-testid="step-modal-close-button" onClick={() => setActive(null)} className="text-[#8FA3B8] hover:text-white p-1"><X size={20} /></button>
                </div>
              </DialogHeader>

              <div className="p-6 space-y-6">
                {step.video && (
                  <button
                    data-testid="step-watch-video-button"
                    onClick={() => setVideoSrc(step.video)}
                    className="w-full flex items-center gap-3 bg-[#0B0F14] border border-white/10 hover:border-[#FF5E1E]/60 p-3 transition-colors group"
                  >
                    <span className="h-10 w-10 grid place-items-center bg-[#FF5E1E] text-[#0B0F14] group-hover:scale-105 transition-transform"><Play size={18} /></span>
                    <span className="text-left">
                      <span className="block font-display font-bold uppercase text-sm">Watch training video</span>
                      <span className="block text-xs text-[#8FA3B8]">Representative footage · ~1 min</span>
                    </span>
                  </button>
                )}

                {step.id === "firewatch" && (
                  <button
                    data-testid="enter-firewatch-course-button"
                    onClick={() => { setActive(null); setCourseOpen(true); }}
                    className="w-full flex items-center gap-3 bg-[#FF5E1E] hover:bg-[#ff7038] text-[#0B0F14] p-3 transition-colors group"
                  >
                    <span className="h-10 w-10 grid place-items-center bg-[#0B0F14] text-[#FF5E1E]"><Icon name="Flame" size={18} /></span>
                    <span className="text-left flex-1">
                      <span className="block font-display font-black uppercase text-sm">Enter the Firewatch Course</span>
                      <span className="block text-xs text-[#0B0F14]/70">4 video lessons · mark complete to unlock OSHA</span>
                    </span>
                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                )}

                <Field label="What it is" value={step.whatItIs} />
                <Field label="Who needs it" value={step.whoNeeds} />

                <div>
                  <div className="font-mono-tech text-[10px] tracking-[0.2em] text-[#FF5E1E] mb-3">
                    {["firewatch", "osha", "verified"].includes(step.id) ? "COVERS / INCLUDES" : "DOCUMENTS & REQUIREMENTS — CHECKLIST"}
                  </div>
                  <div className="space-y-2">
                    {step.documents.map((d, i) => (
                      <label key={i} className="flex items-center gap-3 bg-[#0B0F14] border border-white/10 px-3 py-2.5 cursor-pointer hover:border-white/25 transition-colors">
                        <Checkbox
                          data-testid={`checklist-item-${step.key}-${i}`}
                          checked={!!checked[i]}
                          onCheckedChange={(v) => setChecked((c) => ({ ...c, [i]: v }))}
                          className="border-white/30 data-[state=checked]:bg-[#FF5E1E] data-[state=checked]:border-[#FF5E1E]"
                        />
                        <span className={`text-sm ${checked[i] ? "text-white line-through decoration-[#FF5E1E]" : "text-[#B8C4D0]"}`}>{d}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {step.id === "dbids" && (
                  <div>
                    <div className="font-mono-tech text-[10px] tracking-[0.2em] text-[#FF5E1E] mb-3">INSTALLATION-SPECIFIC REQUIREMENTS</div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {BASES.map((b) => (
                        <button
                          key={b}
                          data-testid={`base-select-${b.slice(0, 6).toLowerCase().replace(/\W/g, "")}`}
                          onClick={() => setBase(b)}
                          className={`text-left text-xs px-3 py-2 border transition-colors ${base === b ? "border-[#FF5E1E] bg-[#FF5E1E]/10 text-white" : "border-white/10 text-[#8FA3B8] hover:border-white/30"}`}
                        >
                          {b}
                        </button>
                      ))}
                    </div>
                    <p className="mt-3 text-xs text-[#8FA3B8]">Selected: <span className="text-white">{base}</span> — enrollment happens at this installation's Visitor Control Center once an employer sponsors you.</p>
                  </div>
                )}

                <Field label="Where enrollment happens" value={step.where} />
                <Field label="What happens after" value={step.after} />

                <div className="flex gap-3 bg-[#1a1206] border border-[#FFB800]/30 p-4">
                  <AlertTriangle size={18} className="text-[#FFB800] shrink-0 mt-0.5" />
                  <p className="text-xs text-[#d9c9a8] leading-relaxed">{step.baseNote}</p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  {step.index === progress ? (
                    <Button data-testid="step-complete-button" onClick={() => completeStep(step.index)} className="flex-1 rounded-none bg-[#10B981] hover:bg-[#0ea472] text-[#0B0F14] font-display font-bold uppercase tracking-wide h-12 group">
                      <Check className="mr-2" size={18} /> {step.index + 1 < PIPELINE.length ? "Complete & Unlock Next" : "Complete — I'm Shipyard Ready"}
                    </Button>
                  ) : (
                    <div className="flex-1 flex items-center justify-center gap-2 border border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981] font-mono-tech text-[11px] tracking-[0.15em] h-12">
                      <Check size={16} /> STAGE COMPLETED
                    </div>
                  )}
                  <Button asChild data-testid="step-modal-start-process-button" variant="outline" className="rounded-none border-[#FF5E1E]/50 bg-transparent hover:bg-[#FF5E1E] hover:text-[#0B0F14] text-white font-display font-bold uppercase tracking-wide h-12 group">
                    <a href="#apply" onClick={() => setActive(null)}>{step.cta} <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} /></a>
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <VideoModal open={!!videoSrc} onClose={() => setVideoSrc(null)} src={videoSrc} title="Training Preview" />
      <FirewatchCourse open={courseOpen} onClose={() => setCourseOpen(false)} />
    </section>
  );
}

function StatusPill({ st, tag }) {
  if (st === "done") return <span className="flex items-center gap-1 font-mono-tech text-[9px] tracking-[0.15em] text-[#10B981] border border-[#10B981]/40 px-1.5 py-0.5"><Check size={10} /> DONE</span>;
  if (st === "current") return <span className="flex items-center gap-1 font-mono-tech text-[9px] tracking-[0.15em] text-[#FF5E1E] border border-[#FF5E1E]/40 px-1.5 py-0.5"><CircleDot size={10} /> CURRENT</span>;
  return <span className="flex items-center gap-1 font-mono-tech text-[9px] tracking-[0.15em] text-[#5b6b7d] border border-white/10 px-1.5 py-0.5"><Lock size={10} /> LOCKED</span>;
}

function Field({ label, value }) {
  return (
    <div>
      <div className="font-mono-tech text-[10px] tracking-[0.2em] text-[#FF5E1E] mb-2">{label}</div>
      <p className="text-sm text-[#B8C4D0] leading-relaxed">{value}</p>
    </div>
  );
}
